import { useState } from 'react'
import { Layers } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { LINK_RULE_COLORS, type EditorLayer, type EditorLayers, type LinkRule } from './editorLayers'

// VISN-360: the editor's Layers menu — three read-only overlays, each off until ticked.

interface EditorLayersMenuProps {
  layers: EditorLayers
  onChange: (layers: EditorLayers) => void
}

export default function EditorLayersMenu({ layers, onChange }: EditorLayersMenuProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const rows: [EditorLayer, string][] = [
    ['flow', t('data_catalog:graph_editor.layer_flow', { defaultValue: 'Flow direction' })],
    ['textLinks', t('data_catalog:graph_editor.layer_text_links', { defaultValue: 'Text links' })],
    ['lineNumbers', t('data_catalog:graph_editor.layer_line_numbers', { defaultValue: 'Line numbers' })],
  ]
  const legend: [LinkRule, string][] = [
    ['flow_trace', t('data_catalog:graph_editor.link_flow_trace', { defaultValue: 'Placed by the flow trace' })],
    ['owner', t('data_catalog:graph_editor.link_owner', { defaultValue: 'Instrument owner' })],
    ['nearest', t('data_catalog:graph_editor.link_nearest', { defaultValue: 'Nearest object' })],
  ]
  const onCount = rows.filter(([key]) => layers[key]).length
  return (
    <div className="absolute top-4 right-4 z-40 flex flex-col items-end text-sm">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 p-3 rounded-xl font-medium bg-white dark:bg-dark-card shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${
          onCount ? 'border-2 border-[#0e7490] dark:border-brand-light text-[#0e7490] dark:text-brand-light' : 'border border-[#e5e5e5] dark:border-dark-border text-foreground'
        }`}
      >
        <Layers className="h-4 w-4" aria-hidden />
        {t('data_catalog:graph_editor.layers', { defaultValue: 'Layers' })}
        {onCount > 0 && <span>({onCount})</span>}
      </button>
      {open && (
        <div className="mt-2 w-64 space-y-2 rounded-xl border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card p-3 shadow-md">
          {rows.map(([key, label]) => (
            <label key={key} className="flex cursor-pointer items-center gap-2 text-foreground">
              <input type="checkbox" checked={layers[key]} onChange={(e) => onChange({ ...layers, [key]: e.target.checked })} />
              {label}
            </label>
          ))}
          {layers.textLinks && (
            <ul className="space-y-1 border-t border-[#e5e5e5] dark:border-dark-border pt-2 text-xs text-muted-foreground">
              {legend.map(([rule, label]) => (
                <li key={rule} className="flex items-center gap-2">
                  <span className="inline-block h-1 w-5 rounded" style={{ backgroundColor: LINK_RULE_COLORS[rule] }} />
                  {label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
